import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";

export interface OrgOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  hubspotOwnerId: string | null;
  managerId: string | null;
}

export interface OwnerNodeData extends Record<string, unknown> {
  owner: OrgOwner;
  isRoot: boolean;
  isAdmin?: boolean;
  onRemove?: (ownerId: string) => void;
  onViewProfile?: (ownerId: string) => void;
  onViewDashboard?: (hubspotOwnerId: string) => void;
}

export const OWNER_DRAG_MIME = "application/org-chart-owner";

const NODE_WIDTH = 280;
const NODE_HEIGHT = 130;

export function getOwnerInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

export function buildNodesAndEdges(
  owners: OrgOwner[],
  rootIds?: Set<string>,
): {
  nodes: Node<OwnerNodeData>[];
  edges: Edge[];
  unassigned: OrgOwner[];
} {
  const ownerMap = new Map(owners.map((o) => [o.id, o]));
  const hasReports = new Set<string>();

  // Identify who has reports
  for (const o of owners) {
    if (o.managerId && ownerMap.has(o.managerId)) {
      hasReports.add(o.managerId);
    }
  }

  const nodes: Node<OwnerNodeData>[] = [];
  const edges: Edge[] = [];
  const unassigned: OrgOwner[] = [];

  for (const o of owners) {
    const isInChart = o.managerId || hasReports.has(o.id) || rootIds?.has(o.id);

    if (!isInChart) {
      unassigned.push(o);
      continue;
    }

    const isRoot = !o.managerId;
    nodes.push({
      id: o.id,
      type: "ownerNode",
      position: { x: 0, y: 0 },
      data: { owner: o, isRoot },
    });

    if (o.managerId && ownerMap.has(o.managerId)) {
      edges.push({
        id: `${o.managerId}-${o.id}`,
        source: o.managerId,
        target: o.id,
        type: "smoothstep",
        selectable: true,
        style: { stroke: "#0E3752", strokeWidth: 2, opacity: 0.5 },
        interactionWidth: 20,
      });
    }
  }

  return { nodes, edges, unassigned };
}

export function layoutTree<T extends Node>(nodes: T[], edges: Edge[]): T[] {
  if (nodes.length === 0) return nodes;

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 80, ranksep: 140 });

  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });
}

export function detectCycle(
  ownerId: string,
  newManagerId: string,
  ownerMap: Map<string, OrgOwner>,
): boolean {
  let currentId: string | null = newManagerId;
  const visited = new Set<string>();
  while (currentId) {
    if (currentId === ownerId) return true;
    if (visited.has(currentId)) return false;
    visited.add(currentId);
    currentId = ownerMap.get(currentId)?.managerId ?? null;
  }
  return false;
}
